import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';
import type { KindCode } from '../foundInDocuments.ts';

/** Section R (176): "Found in the documents". Every call is a POST; nothing rides in a URL. The routes decide who. */
export interface ReviewKind {
  code: KindCode | 'DATE_DIFFERS';
  label: string;
  basis: 'record' | 'reading' | 'list';
  may_auto: boolean;
  on: boolean;
  setting: { enabled: boolean; at: string; by: string | null } | null;
}
export interface Yard { closes: number; with_paper: number; agrees: number }
export interface ReviewClient { id: string; client_ref: string; last_name: string; first_name: string | null }
export interface ReviewOrder { id: string; signed_date: string | null; type: string | null }
export interface ReviewRow {
  deadline_id?: string;
  field_id?: string | null;
  on_date?: string;
  proposed?: string;
  page_no?: number | null;
  clio_copy_id?: string;
  court_index_number?: string;
  read_date?: string;
  on_file?: string | null;
  client: ReviewClient | null;
  court_order?: ReviewOrder | null;
}

const post = <T,>(getToken: () => Promise<string>, path: string, body: unknown) => apiFetch<T>(path, getToken, {
  method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
});

export function useReviewSummary() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['reading-review', 'summary'],
    queryFn: () => post<{ kinds: ReviewKind[]; may_set_toggle: boolean; yardstick: Record<'P_OATH' | 'P_COMMISSION', Yard | null> }>(
      getToken, '/reading-review/summary', {}),
    ...queryDefaults,
  });
}

export function useReviewRows(kind: KindCode | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['reading-review', 'rows', kind],
    queryFn: () => post<{ rows: ReviewRow[] }>(getToken, '/reading-review/rows', { kind }),
    ...queryDefaults,
    enabled: kind !== null,
  });
}

export function useClosedByRule(kind: KindCode | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['reading-review', 'closed', kind],
    queryFn: () => post<{ rows: (ReviewRow & { completed_on: string; decided_at: string })[] }>(
      getToken, '/reading-review/closed-by-rule', { kind }),
    ...queryDefaults,
    enabled: kind !== null,
  });
}

const invalidate = (qc: ReturnType<typeof useQueryClient>) => {
  void qc.invalidateQueries({ queryKey: ['reading-review'] });
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
  void qc.invalidateQueries({ queryKey: ['client'] });
};

export function useConfirmRows() {
  const { getToken } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { kind: KindCode; rows: { deadline_id: string; field_id: string | null }[] }) =>
      post<{ results: { deadline_id: string; recorded: boolean; why?: string }[]; recorded: number; not_recorded: number }>(
        getToken, '/reading-review/confirm', a),
    onSuccess: () => invalidate(qc),
  });
}

export function useNotRight() {
  const { getToken } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { kind: KindCode; deadline_id: string; field_id: string | null; why: string }) =>
      post<{ recorded: boolean }>(getToken, '/reading-review/not-right', a),
    onSuccess: () => invalidate(qc),
  });
}

export function useSetAutoclose() {
  const { getToken } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { kind: KindCode; enabled: boolean; reason: string | null }) =>
      post<{ recorded: boolean }>(getToken, '/reading-review/autoclose', a),
    onSuccess: () => invalidate(qc),
  });
}
